class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      '[data-predictive-search]'
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.searchModal = this.closest('.search-modal');
    this.searchDetails = this.closest('details');

    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.viewportUpdateFrame = null;
    this.pageLockState = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener(
      'submit',
      this.onFormSubmit.bind(this)
    );

    this.input.addEventListener(
      'focus',
      this.onFocus.bind(this)
    );

    this.addEventListener(
      'focusout',
      this.onFocusOut.bind(this)
    );

    this.addEventListener(
      'keyup',
      this.onKeyup.bind(this)
    );

    this.addEventListener(
      'keydown',
      this.onKeydown.bind(this)
    );

    this.onSearchModalClickHandler =
      this.onSearchModalClick.bind(this);

    this.onDetailsToggleHandler =
      this.onDetailsToggle.bind(this);

    this.onViewportChangeHandler =
      this.onViewportChange.bind(this);

    this.searchModal?.addEventListener(
      'click',
      this.onSearchModalClickHandler
    );

    this.searchDetails?.addEventListener(
      'toggle',
      this.onDetailsToggleHandler
    );

    window.addEventListener(
      'resize',
      this.onViewportChangeHandler
    );

    window.visualViewport?.addEventListener(
      'resize',
      this.onViewportChangeHandler
    );

    window.visualViewport?.addEventListener(
      'scroll',
      this.onViewportChangeHandler
    );
  }

  disconnectedCallback() {
    this.searchModal?.removeEventListener(
      'click',
      this.onSearchModalClickHandler
    );

    this.searchDetails?.removeEventListener(
      'toggle',
      this.onDetailsToggleHandler
    );

    window.removeEventListener(
      'resize',
      this.onViewportChangeHandler
    );

    window.visualViewport?.removeEventListener(
      'resize',
      this.onViewportChangeHandler
    );

    window.visualViewport?.removeEventListener(
      'scroll',
      this.onViewportChangeHandler
    );

    if (this.viewportUpdateFrame) {
      window.cancelAnimationFrame(
        this.viewportUpdateFrame
      );
    }

    this.unlockPageScroll();
  }

  getQuery() {
    return this.input.value.trim();
  }

  isMobileSearch() {
    return window.matchMedia(
      '(max-width: 749px)'
    ).matches;
  }

  normalizeText(value) {
    return String(value || '')
      .normalize('NFC')
      .toLocaleLowerCase('de-DE')
      .trim();
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    if (
      !this.searchTerm ||
      !newSearchTerm.startsWith(this.searchTerm)
    ) {
      this.querySelector(
        '#predictive-search-results-groups-wrapper'
      )?.remove();
    }

    this.updateSearchForTerm(
      this.searchTerm,
      newSearchTerm
    );

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';

      this.abortController.abort();
      this.abortController =
        new AbortController();

      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (
      this.getAttribute('results') === 'true'
    ) {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.contains(document.activeElement)
      ) {
        return;
      }

      const keepMobileResultsOpen =
        this.isMobileSearch() &&
        this.searchDetails?.open &&
        this.getQuery().length > 0 &&
        this.getAttribute('results') === 'true';

      if (keepMobileResultsOpen) {
        this.open();
        return;
      }

      this.close();
    });
  }

  onDetailsToggle() {
    if (
      this.searchDetails?.open &&
      this.isMobileSearch()
    ) {
      this.lockPageScroll();
      return;
    }

    this.unlockPageScroll();
  }

  lockPageScroll() {
    if (
      !this.isMobileSearch() ||
      this.pageLockState
    ) {
      return;
    }

    if (
      PredictiveSearch.mobileLockOwner &&
      PredictiveSearch.mobileLockOwner !== this
    ) {
      return;
    }

    const scrollY =
      window.scrollY ||
      document.documentElement.scrollTop ||
      0;

    this.pageLockState = {
      scrollY,
      htmlStyle:
        document.documentElement.getAttribute(
          'style'
        ),
      bodyStyle:
        document.body.getAttribute('style'),
    };

    PredictiveSearch.mobileLockOwner = this;

    document.documentElement.classList.add(
      'gt-search-scroll-lock'
    );

    document.body.classList.add(
      'gt-search-scroll-lock'
    );

    document.documentElement.style.overflow =
      'hidden';

    document.documentElement.style
      .overscrollBehavior = 'none';

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior =
      'none';

    this.updateMobileViewportHeight();
  }

  unlockPageScroll() {
    if (
      !this.pageLockState ||
      PredictiveSearch.mobileLockOwner !== this
    ) {
      return;
    }

    const {
      scrollY,
      htmlStyle,
      bodyStyle,
    } = this.pageLockState;

    document.documentElement.classList.remove(
      'gt-search-scroll-lock'
    );

    document.body.classList.remove(
      'gt-search-scroll-lock'
    );

    if (htmlStyle === null) {
      document.documentElement.removeAttribute(
        'style'
      );
    } else {
      document.documentElement.setAttribute(
        'style',
        htmlStyle
      );
    }

    if (bodyStyle === null) {
      document.body.removeAttribute('style');
    } else {
      document.body.setAttribute(
        'style',
        bodyStyle
      );
    }

    this.searchModal?.style.removeProperty(
      '--gt-search-viewport-height'
    );

    this.pageLockState = null;
    PredictiveSearch.mobileLockOwner = null;

    window.scrollTo(0, scrollY);
  }

  updateMobileViewportHeight() {
    if (
      !this.searchModal ||
      !this.isMobileSearch()
    ) {
      return;
    }

    const viewportHeight =
      window.visualViewport?.height ||
      window.innerHeight;

    this.searchModal.style.setProperty(
      '--gt-search-viewport-height',
      `${Math.round(viewportHeight)}px`
    );
  }

  onSearchModalClick(event) {
    if (!this.isMobileSearch()) return;

    const target = event.target;

    const isEmptySurface =
      target === this.searchModal ||
      target?.classList?.contains(
        'modal-overlay'
      ) ||
      target?.classList?.contains(
        'search-modal__content'
      );

    if (!isEmptySurface) return;

    const detailsModal =
      this.closest('details-modal');

    if (
      typeof detailsModal?.close ===
      'function'
    ) {
      detailsModal.close(false);
    } else {
      this.close();
      this.searchDetails?.removeAttribute(
        'open'
      );
    }
  }

  onViewportChange() {
    if (
      this.searchDetails?.open &&
      this.isMobileSearch()
    ) {
      this.lockPageScroll();
      this.updateMobileViewportHeight();
    } else {
      this.unlockPageScroll();
    }

    if (!this.isOpen) return;

    if (this.viewportUpdateFrame) {
      window.cancelAnimationFrame(
        this.viewportUpdateFrame
      );
    }

    this.viewportUpdateFrame =
      window.requestAnimationFrame(() => {
        this.updateResultsMaxHeight();
        this.viewportUpdateFrame = null;
      });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;

      case 'ArrowDown':
        this.switchOption('down');
        break;

      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(
    previousTerm,
    newTerm
  ) {
    if (!previousTerm) return;

    const searchForTextElement =
      this.querySelector(
        '[data-predictive-search-search-for-text]'
      );

    const currentButtonText =
      searchForTextElement?.innerText;

    if (!currentButtonText) return;

    const firstMatch =
      currentButtonText.indexOf(previousTerm);

    const secondMatch =
      currentButtonText.indexOf(
        previousTerm,
        firstMatch + previousTerm.length
      );

    if (
      firstMatch === -1 ||
      secondMatch !== -1
    ) {
      return;
    }

    searchForTextElement.innerText =
      currentButtonText.replace(
        previousTerm,
        newTerm
      );
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';

    const selectedElement =
      this.querySelector(
        '[aria-selected="true"]'
      );

    const allVisibleElements = Array.from(
      this.querySelectorAll(
        'li[role="option"], button.predictive-search__item'
      )
    ).filter(
      (element) =>
        element.offsetParent !== null
    );

    if (
      !allVisibleElements.length ||
      (moveUp && !selectedElement)
    ) {
      return;
    }

    const selectedElementIndex =
      allVisibleElements.indexOf(
        selectedElement
      );

    let activeElementIndex = 0;

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex ===
        allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (
      activeElementIndex ===
      selectedElementIndex
    ) {
      return;
    }

    const activeElement =
      allVisibleElements[activeElementIndex];

    activeElement.setAttribute(
      'aria-selected',
      'true'
    );

    selectedElement?.setAttribute(
      'aria-selected',
      'false'
    );

    this.input.setAttribute(
      'aria-activedescendant',
      activeElement.id
    );
  }

  selectOption() {
    const selectedOption =
      this.querySelector(
        '[aria-selected="true"] a, button[aria-selected="true"]'
      );

    selectedOption?.click();
  }

  getSearchResults(searchTerm) {
    const queryKey =
      this.normalizeText(searchTerm);

    this.setLiveRegionLoadingState();

    this.abortController.abort();
    this.abortController =
      new AbortController();

    const requestSignal =
      this.abortController.signal;

    const markupPromise =
      this.cachedResults[queryKey]
        ? Promise.resolve(
            this.cachedResults[queryKey]
          )
        : this.fetchPredictiveMarkup(
            searchTerm,
            requestSignal
          );

    Promise.all([
      markupPromise,
      this.getCompleteProductCatalog()
        .catch(() => null),
    ])
      .then(
        ([
          resultsMarkup,
          completeCatalog,
        ]) => {
          if (
            this.getQuery() !== searchTerm
          ) {
            return;
          }

          this.allPredictiveSearchInstances
            .forEach(
              (predictiveSearchInstance) => {
                predictiveSearchInstance
                  .cachedResults[queryKey] =
                  resultsMarkup;
              }
            );

          this.renderSearchResults(
            resultsMarkup,
            completeCatalog,
            searchTerm
          );
        }
      )
      .catch((error) => {
        if (
          error?.name === 'AbortError' ||
          error?.code === 20
        ) {
          return;
        }

        this.close();

        console.error(
          'GREAT/TOWN predictive search:',
          error
        );
      });
  }

  fetchPredictiveMarkup(
    searchTerm,
    signal
  ) {
    const searchParams =
      new URLSearchParams({
        q: searchTerm,
        'resources[type]':
          'product,collection',
        'resources[limit]': '10',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]':
          'last',
        'resources[options][fields]':
          'title',
        section_id: 'predictive-search',
      });

    return fetch(
      `${routes.predictive_search_url}?${searchParams.toString()}`,
      { signal }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Predictive search request failed: ${response.status}`
          );
        }

        return response.text();
      })
      .then((text) => {
        const section =
          new DOMParser()
            .parseFromString(
              text,
              'text/html'
            )
            .querySelector(
              '#shopify-section-predictive-search'
            );

        if (!section) {
          throw new Error(
            'Predictive search section was not returned.'
          );
        }

        return section.innerHTML;
      });
  }

  getCompleteProductCatalog() {
    if (
      !PredictiveSearch.productCatalogPromise
    ) {
      PredictiveSearch.productCatalogPromise =
        this.loadCompleteProductCatalog()
          .catch((error) => {
            PredictiveSearch.productCatalogPromise =
              null;

            throw error;
          });
    }

    return PredictiveSearch
      .productCatalogPromise;
  }

  async loadCompleteProductCatalog() {
    const root =
      window.Shopify?.routes?.root || '/';

    const normalizedRoot =
      root.endsWith('/')
        ? root
        : `${root}/`;

    const endpoints = [
      `${normalizedRoot}collections/all/products.json`,
      `${normalizedRoot}products.json`,
    ];

    let lastError;

    for (const endpoint of endpoints) {
      try {
        const products =
          await this.loadCatalogPages(
            endpoint
          );

        if (products.length) {
          return products;
        }
      } catch (error) {
        lastError = error;
      }
    }

    throw (
      lastError ||
      new Error(
        'The storefront product catalog could not be loaded.'
      )
    );
  }

  async loadCatalogPages(endpoint) {
    const pageSize = 250;
    const maximumPages = 40;
    const products = [];
    const knownProducts = new Set();

    let previousPageSignature = '';

    for (
      let page = 1;
      page <= maximumPages;
      page += 1
    ) {
      const url = new URL(
        endpoint,
        window.location.origin
      );

      url.searchParams.set(
        'limit',
        String(pageSize)
      );

      url.searchParams.set(
        'page',
        String(page)
      );

      const response = await fetch(
        url.toString(),
        {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Product catalog request failed: ${response.status}`
        );
      }

      const payload =
        await response.json();

      const batch =
        Array.isArray(payload?.products)
          ? payload.products
          : [];

      if (!batch.length) break;

      const pageSignature =
        batch
          .map(
            (product) =>
              product.handle ||
              product.id
          )
          .join('|');

      if (
        pageSignature ===
        previousPageSignature
      ) {
        break;
      }

      previousPageSignature =
        pageSignature;

      batch.forEach((product) => {
        const productKey =
          product.id ||
          product.handle;

        if (
          !productKey ||
          knownProducts.has(productKey)
        ) {
          return;
        }

        knownProducts.add(productKey);
        products.push(product);
      });

      if (batch.length < pageSize) {
        break;
      }
    }

    return products;
  }

  renderSearchResults(
    resultsMarkup,
    completeCatalog,
    searchTerm
  ) {
    this.predictiveSearchResults.innerHTML =
      resultsMarkup;

    if (Array.isArray(completeCatalog)) {
      this.renderExactProductMatches(
        completeCatalog,
        searchTerm
      );
    }

    this.setAttribute('results', 'true');
    this.setLiveRegionResults();
    this.open();
  }

  renderExactProductMatches(
    products,
    searchTerm
  ) {
    const productList =
      this.querySelector(
        '[data-gt-exact-product-list]'
      );

    if (!productList) return;

    const normalizedTerm =
      this.normalizeText(searchTerm);

    const exactMatches =
      products.filter((product) =>
        this
          .normalizeText(product.title)
          .includes(normalizedTerm)
      );

    productList.replaceChildren();

    exactMatches.forEach(
      (product, index) => {
        productList.appendChild(
          this.createProductResult(
            product,
            index
          )
        );
      }
    );

    const emptyMessage =
      this.querySelector(
        '[data-gt-no-products]'
      );

    if (emptyMessage) {
      emptyMessage.hidden =
        exactMatches.length > 0;
    }

    const productGroup =
      this.querySelector(
        '[data-gt-product-group]'
      );

    productGroup?.classList.toggle(
      'gt-predictive-search__product-group--empty',
      exactMatches.length === 0
    );

    const collectionCount =
      this.querySelectorAll(
        '[data-gt-collection-result]'
      ).length;

    const liveRegionValue =
      this.querySelector(
        '[data-predictive-search-live-region-count-value]'
      );

    if (liveRegionValue) {
      const total =
        exactMatches.length +
        collectionCount;

      liveRegionValue.textContent =
        `${total} Ergebnisse: ` +
        `${exactMatches.length} Produkte, ` +
        `${collectionCount} Kollektionen`;
    }
  }

  createProductResult(product, index) {
    const listItem =
      document.createElement('li');

    listItem.id =
      `predictive-search-option-product-${index + 1}`;

    listItem.className =
      'predictive-search__list-item';

    listItem.setAttribute(
      'role',
      'option'
    );

    listItem.setAttribute(
      'aria-selected',
      'false'
    );

    const link =
      document.createElement('a');

    const root =
      window.Shopify?.routes?.root || '/';

    const normalizedRoot =
      root.endsWith('/')
        ? root
        : `${root}/`;

    link.href =
      `${normalizedRoot}products/${product.handle}`;

    link.className =
      'predictive-search__item ' +
      'predictive-search__item--link-with-thumbnail ' +
      'link link--text';

    link.tabIndex = -1;

    const imageSource =
      this.getProductImageSource(product);

    if (imageSource) {
      const image =
        document.createElement('img');

      image.className =
        'predictive-search__image';

      image.src =
        this.getSizedImageUrl(
          imageSource,
          180
        );

      image.alt = product.title || '';
      image.width = 80;
      image.height = 80;
      image.loading = 'lazy';

      link.appendChild(image);
    } else {
      const placeholder =
        document.createElement('span');

      placeholder.className =
        'gt-predictive-search__image-placeholder';

      placeholder.setAttribute(
        'aria-hidden',
        'true'
      );

      link.appendChild(placeholder);
    }

    const content =
      document.createElement('div');

    content.className =
      'predictive-search__item-content ' +
      'predictive-search__item-content--centered';

    const title =
      document.createElement('p');

    title.className =
      'predictive-search__item-heading h5';

    title.textContent =
      product.title || '';

    content.appendChild(title);
    link.appendChild(content);
    listItem.appendChild(link);

    return listItem;
  }

  getProductImageSource(product) {
    if (
      typeof product.featured_image ===
      'string'
    ) {
      return product.featured_image;
    }

    if (product.featured_image?.src) {
      return product.featured_image.src;
    }

    if (product.image?.src) {
      return product.image.src;
    }

    const firstImage =
      product.images?.[0];

    if (typeof firstImage === 'string') {
      return firstImage;
    }

    return firstImage?.src || '';
  }

  getSizedImageUrl(source, width) {
    try {
      const url = new URL(
        source,
        window.location.origin
      );

      url.searchParams.set(
        'width',
        String(width)
      );

      return url.toString();
    } catch (_error) {
      return source;
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector(
        '.predictive-search-status'
      );

    this.loadingText =
      this.loadingText ||
      this.getAttribute(
        'data-loading-text'
      );

    this.setLiveRegionText(
      this.loadingText
    );

    this.setAttribute(
      'loading',
      'true'
    );
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute(
      'aria-hidden',
      'false'
    );

    this.statusElement.textContent =
      statusText || '';

    setTimeout(() => {
      this.statusElement?.setAttribute(
        'aria-hidden',
        'true'
      );
    }, 1000);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const resultText =
      this.querySelector(
        '[data-predictive-search-live-region-count-value]'
      )?.textContent || '';

    this.setLiveRegionText(resultText);
  }

  getResultsMaxHeight() {
    const headerBottom =
      document
        .querySelector('.section-header')
        ?.getBoundingClientRect()
        .bottom || 0;

    this.resultsMaxHeight =
      Math.max(
        200,
        window.innerHeight - headerBottom
      );

    return this.resultsMaxHeight;
  }

  updateResultsMaxHeight() {
    if (this.isMobileSearch()) {
      this.resultsMaxHeight = false;

      this.predictiveSearchResults.style
        .removeProperty('max-height');

      return;
    }

    const maxHeight =
      this.getResultsMaxHeight();

    this.predictiveSearchResults.style
      .maxHeight =
        `${Math.floor(maxHeight)}px`;
  }

  open() {
    this.updateResultsMaxHeight();

    this.setAttribute('open', 'true');

    this.input.setAttribute(
      'aria-expanded',
      'true'
    );

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    this.querySelector(
      '[aria-selected="true"]'
    )?.setAttribute(
      'aria-selected',
      'false'
    );

    this.input.setAttribute(
      'aria-activedescendant',
      ''
    );

    this.input.setAttribute(
      'aria-expanded',
      'false'
    );

    this.removeAttribute('loading');
    this.removeAttribute('open');

    this.resultsMaxHeight = false;

    this.predictiveSearchResults
      .removeAttribute('style');
  }
}

PredictiveSearch.productCatalogPromise = null;
PredictiveSearch.mobileLockOwner = null;

customElements.define(
  'predictive-search',
  PredictiveSearch
);